import { Fragment, useCallback, useEffect, useState } from "react";
import {
  coachBriefClause,
  coachBriefMissingSentence,
  coachBriefParagraph,
  composeCoachBrief,
  type CoachBrief,
  type CoachBriefLine,
} from "@care-by-ella/contracts";
import {
  fetchCoachMemory,
  ForbiddenError,
  UnauthorizedError,
} from "@/lib/clinician-api";
import {
  F01_ORIGINALS_ANCHOR,
  memoryItemAnchor,
  PATIENT_MEMORY_ANCHOR,
} from "@/lib/coach-brief-anchors";

// TES-1074 points 2–4 — "Patienten i korthet", the coach's primary overview,
// placed before Patientminnet.
//
// It reads the same GET …/memory as Patientminnet (@Roles coach, doctor and
// the admin roles) and composes it with `composeCoachBrief`: one line per
// memory row, quoted as stored, never rephrased. Each line opens its row in
// Patientminnet below (source, source date, last updated, type), and an F-01
// line also opens the original answer. An empty section says it is missing.
//
// TES-1211 — and the lines became one paragraph (coachBriefParagraph): a
// sentence per section, the label as its run-in head, the newest lines
// quoted with a numbered footnote each. "Visa källor" lists the footnotes
// with source, date and, for F-01, the original answer; what is not known
// is named in one closing sentence. Nothing is generated or stored.
//
// `refreshKey` changes whenever Patientminnet is written to, so a correction
// there shows here without a page reload.

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("sv-SE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function PatientInBrief({
  patientId,
  refreshKey,
  onUnauthorized,
}: {
  patientId: string;
  refreshKey: number;
  onUnauthorized?: () => void;
}) {
  const [brief, setBrief] = useState<CoachBrief | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    try {
      setBrief(composeCoachBrief(await fetchCoachMemory(patientId)));
      setError(null);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized?.();
        return;
      }
      if (e instanceof ForbiddenError) {
        setForbidden(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [patientId, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (forbidden) return null;

  return (
    <section
      aria-labelledby="patienten-i-korthet"
      className="rounded-md border border-surfaceMuted bg-surface p-md flex flex-col gap-md"
    >
      <div>
        <h2 id="patienten-i-korthet" className="font-display text-lg">
          Patienten i korthet
        </h2>
        {/* Ö-754 (Emelie 2026-10-21): the approved intro replaces the claim
            that everything is verbatim and nothing interpreted, which the
            summarised clauses do not support. */}
        <p className="text-xs text-textSecondary">
          Här visas korta, källstödda sammanfattningar. Öppna källorna för att
          kontrollera underlaget.
        </p>
      </div>

      {error ? (
        <p className="text-sm text-danger">Kunde inte hämta: {error}</p>
      ) : !brief ? (
        <p className="text-sm text-textSecondary">Hämtar…</p>
      ) : (
        <BriefBody brief={brief} />
      )}
    </section>
  );
}

// Exported for its render test.
export function BriefBody({ brief }: { brief: CoachBrief }) {
  const [showSources, setShowSources] = useState(false);
  const paragraph = coachBriefParagraph(brief);
  const missing = coachBriefMissingSentence(paragraph.missing);
  // Footnotes are numbered in reading order.
  const sources: CoachBriefLine[] = paragraph.sentences.flatMap((s) => s.lines);
  const numberOf = new Map(sources.map((l, i) => [l.itemId, i + 1]));

  return (
    <>
      <p className="max-w-3xl text-sm leading-relaxed text-textPrimary">
        {paragraph.sentences.map((sentence) => {
          const last = coachBriefClause(
            sentence.lines[sentence.lines.length - 1].body,
          );
          const stop = sentence.more === 0 && last.endsWith("?") ? " " : ". ";
          return (
            <span key={sentence.key}>
              <strong className="font-semibold">{sentence.label}:</strong>{" "}
              {sentence.lines.map((line, i) => (
                <Fragment key={line.itemId}>
                  {i > 0 ? "; " : null}
                  {coachBriefClause(line.body)}
                  <sup>
                    <a
                      href={`#${memoryItemAnchor(line.itemId)}`}
                      aria-label={`Källa ${numberOf.get(line.itemId)}: ${line.sourceLabel}, ${fmtDay(line.observedAt)}`}
                      className="ml-[1px] text-brand hover:underline"
                    >
                      {numberOf.get(line.itemId)}
                    </a>
                  </sup>
                </Fragment>
              ))}
              {sentence.more > 0 ? (
                <>
                  {" ("}
                  <a
                    href={`#${PATIENT_MEMORY_ANCHOR}`}
                    className="text-brand hover:underline"
                  >
                    {sentence.more === 1
                      ? "1 notering till i Patientminnet"
                      : `${sentence.more} fler noteringar i Patientminnet`}
                  </a>
                  {")"}
                </>
              ) : null}
              {stop}
            </span>
          );
        })}
        {missing ? <span className="text-textSecondary">{missing}</span> : null}
      </p>

      {sources.length > 0 ? (
        <div>
          <button
            type="button"
            aria-expanded={showSources}
            onClick={() => setShowSources((v) => !v)}
            className="text-xs text-brand hover:opacity-80"
          >
            {showSources ? "Dölj källor" : `Visa källor (${sources.length})`}
          </button>
          {showSources ? (
            <ol className="mt-xs flex flex-col gap-xs">
              {sources.map((line, i) => (
                <li key={line.itemId} className="flex gap-xs text-sm">
                  <span className="w-md shrink-0 text-xs text-textSecondary">
                    {i + 1}.
                  </span>
                  <span className="min-w-0">
                    <span className="text-textPrimary">{line.body}</span>
                    <span className="block text-xs text-textSecondary">
                      <a
                        href={`#${memoryItemAnchor(line.itemId)}`}
                        className="text-brand hover:underline"
                      >
                        {line.sourceLabel} · {fmtDay(line.observedAt)}
                      </a>
                      {line.fromF01 ? (
                        <>
                          {" · "}
                          <a
                            href={`#${F01_ORIGINALS_ANCHOR}`}
                            className="text-brand hover:underline"
                          >
                            Originalsvar
                          </a>
                        </>
                      ) : null}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      ) : null}

      {paragraph.unconfirmedHypotheses > 0 ? (
        <p className="text-xs text-textSecondary">
          {paragraph.unconfirmedHypotheses === 1
            ? "1 obekräftad AI-observation"
            : `${paragraph.unconfirmedHypotheses} obekräftade AI-observationer`}{" "}
          visas inte här förrän du bekräftat den i Patientminnet.
        </p>
      ) : null}
    </>
  );
}
